import * as React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useOptimisationRuns } from '@/hooks/optimisation/use-optimisation-runs';
import { useOptimisationRun } from '@/hooks/optimisation/use-optimisation-run';
import { RunStatusBadge } from './run-status-badge';
import { OptimiseButton } from './optimise-button';

interface LatestRunPanelProps {
  projectId: number;
  isDeleted: boolean;
}

function formatMm(mm: number | null): string {
  if (mm == null) return '—';
  return mm >= 1000 ? `${(mm / 1000).toFixed(2)} m` : `${mm} mm`;
}

export function LatestRunPanel({ projectId, isDeleted }: LatestRunPanelProps) {
  const { data: runs, isLoading: runsLoading } = useOptimisationRuns({ projectId, limit: 1 });
  const latestRunId = runs?.items[0]?.id;

  // Polling hook — handles QUEUED/RUNNING auto-refresh
  const { data: run, isLoading: runLoading } = useOptimisationRun(latestRunId);

  if (runsLoading || runLoading) {
    return (
      <Card>
        <CardContent className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Optimisation</CardTitle>
          {!isDeleted && <OptimiseButton projectId={projectId} />}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!run ? (
          <p className="text-sm text-fg-muted">No optimisation runs yet.</p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <RunStatusBadge status={run.runStatus} />
              <span className="text-xs text-fg-muted font-mono">Run #{run.id}</span>
            </div>

            {run.runStatus === 'COMPLETED' && (
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-fg-muted text-xs">Bars used</p>
                  <p className="font-semibold">{run.totalBarsUsed ?? '—'}</p>
                </div>
                <div>
                  <p className="text-fg-muted text-xs">Total waste</p>
                  <p className="font-semibold">{formatMm(run.totalWasteMm)}</p>
                </div>
                <div>
                  <p className="text-fg-muted text-xs">Algorithm</p>
                  <p className="font-semibold">{run.algorithmUsed ?? '—'}</p>
                </div>
              </div>
            )}

            {run.runStatus === 'FAILED' && (
              <p className="text-sm text-destructive">{run.errorText ?? 'Run failed.'}</p>
            )}

            <Button variant="link" size="sm" className="h-auto p-0" asChild>
              <Link to={`/optimisation/runs/${run.id}`}>
                View full run
                <ExternalLink className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </>
        )}

        {runs && runs.items.length > 0 && (
          <Button variant="link" size="sm" className="h-auto p-0" asChild>
            <Link to={`/optimisation/runs?projectId=${projectId}`}>
              All runs ({runs.items.length}+)
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
